const DEFAULTS = {
  userId: 'mock-user-id',
  name: 'Mock User',
  roles: ['user'],
  clientId: 'mock-client',
  issuer: 'https://mock-keycloak.local/realms/mock',
}

const MOCK_COOKIE_PREFIX = 'e2e-mock-'

// Central cookie schema for mock auth. Both reads and cleanup derive from this map
// so future fields do not get out of sync across the helper.
const MOCK_COOKIE_FIELDS = {
  auth: { name: `${MOCK_COOKIE_PREFIX}auth`, fallback: 'false' },
  userId: { name: `${MOCK_COOKIE_PREFIX}user-id`, fallback: DEFAULTS.userId },
  roles: {
    name: `${MOCK_COOKIE_PREFIX}roles`,
    fallback: DEFAULTS.roles.join(','),
  },
  email: { name: `${MOCK_COOKIE_PREFIX}email`, fallback: null },
  name: { name: `${MOCK_COOKIE_PREFIX}name`, fallback: DEFAULTS.name },
  preferredUsername: {
    name: `${MOCK_COOKIE_PREFIX}preferred-username`,
    fallback: null,
  },
}

const decodeCookieValue = (value) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const readCookieSnapshot = () => {
  if (typeof document === 'undefined') {
    return {}
  }

  return document.cookie.split('; ').reduce((cookies, rawCookie) => {
    if (!rawCookie) {
      return cookies
    }

    const separatorIndex = rawCookie.indexOf('=')
    const name =
      separatorIndex === -1 ? rawCookie : rawCookie.slice(0, separatorIndex)
    const value =
      separatorIndex === -1 ? '' : rawCookie.slice(separatorIndex + 1)

    cookies[name] = decodeCookieValue(value)
    return cookies
  }, {})
}

const getCookieFieldValue = (cookies, fieldName) => {
  const field = MOCK_COOKIE_FIELDS[fieldName]

  if (!field) {
    throw new Error(`Unknown mock cookie field: ${fieldName}`)
  }

  return cookies[field.name] ?? field.fallback
}

const encodeBase64Url = (value) => {
  const utf8Bytes = new TextEncoder().encode(value)
  const binary = Array.from(utf8Bytes, (byte) =>
    String.fromCharCode(byte)
  ).join('')

  const encodedToken = btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '')

  return encodedToken
}

const deriveNameParts = (name) => {
  const parts = name.trim().split(/\s+/).filter(Boolean)

  return {
    givenName: parts[0] || 'Mock',
    familyName: parts.length > 1 ? parts.slice(1).join(' ') : 'User',
  }
}

const parseMockRoles = (rawRoles = DEFAULTS.roles.join(',')) =>
  rawRoles
    .split(',')
    .map((role) => role.trim())
    .filter(Boolean)

const getIssuer = ({ keycloakUrl, keycloakRealm }) =>
  keycloakUrl && keycloakRealm
    ? `${keycloakUrl.replace(/\/$/, '')}/realms/${keycloakRealm}`
    : DEFAULTS.issuer

export const hasMockAuthCookie = () => {
  const cookies = readCookieSnapshot()
  return getCookieFieldValue(cookies, 'auth') === 'true'
}

export const buildMockKeycloakIdentity = (publicConfig = {}) => {
  const cookies = readCookieSnapshot()
  const userId = getCookieFieldValue(cookies, 'userId')
  const name = getCookieFieldValue(cookies, 'name')
  const { givenName, familyName } = deriveNameParts(name)
  const preferredUsername =
    getCookieFieldValue(cookies, 'preferredUsername') || userId
  const email =
    getCookieFieldValue(cookies, 'email') || `${preferredUsername}@laniqo.test`
  const roles = parseMockRoles(getCookieFieldValue(cookies, 'roles'))
  const issuedAt = Math.floor(Date.now() / 1000)
  const expiresAt = issuedAt + 60 * 60
  const clientId = publicConfig.keycloakClientId || DEFAULTS.clientId
  const tokenPayload = {
    sub: userId,
    email,
    preferred_username: preferredUsername,
    name,
    given_name: givenName,
    family_name: familyName,
    realm_access: { roles },
    azp: clientId,
    iss: getIssuer(publicConfig),
    iat: issuedAt,
    exp: expiresAt,
  }
  const tokenHeader = {
    alg: 'none', // mock only; never use for real auth
    typ: 'JWT',
  }

  return {
    clientId,
    tokenParsed: tokenPayload,
    token: `${encodeBase64Url(JSON.stringify(tokenHeader))}.${encodeBase64Url(JSON.stringify(tokenPayload))}.`,
  }
}

export const syncMockKeycloakState = (keycloak, publicConfig = {}) => {
  const identity = buildMockKeycloakIdentity(publicConfig)
  keycloak.authenticated = true
  keycloak.clientId = identity.clientId
  keycloak.token = identity.token
  keycloak.tokenParsed = identity.tokenParsed
  return identity
}

export const clearMockAuthCookies = () => {
  if (typeof document === 'undefined') {
    return
  }

  for (const field of Object.values(MOCK_COOKIE_FIELDS)) {
    document.cookie = `${field.name}=; Max-Age=0; path=/`
  }
}

export const createMockKeycloak = (publicConfig = {}) => {
  const mockKeycloak = {
    authenticated: true,
    clientId: publicConfig.keycloakClientId || DEFAULTS.clientId,
    token: '',
    tokenParsed: {},
    login: () => {},
    logout: () => {
      clearMockAuthCookies()
      if (typeof window !== 'undefined') {
        window.location.reload()
      }
    },
    updateToken: () => {
      syncMockKeycloakState(mockKeycloak, publicConfig)
      return Promise.resolve(true)
    },
  }

  syncMockKeycloakState(mockKeycloak, publicConfig)
  return mockKeycloak
}
